/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { type AbstractControl } from '@angular/forms';
import {
type ColumnDef, type Command, type TableConfig, BatchService, DataTableComponent,
DMValidators,
FormControl, FormGroup, PromptService } from '@dm/core';
import { type CanLeave } from 'app/common/guards';
import { SWITCH } from 'app/config';

import { type DnsService } from '../../../../common/types/mo';
import { type DnsDomains } from './file-dns-service.d';
import { FileDnsServiceService } from './file-dns-service.service';
import { type ControlValer, controlKeyPrefix, FileDnsServiceStrService, indexKey, MAXDNSNUMBER } from './file-dns-service-str.service';

@Component({
    selector: 'app-file-dns-service',
    templateUrl: './file-dns-service.component.html',
    styleUrls: ['./file-dns-service.component.scss'],
})
export class FileDnsServiceComponent implements CanLeave, OnInit {
    /**
     * 是否展示极简样式
     */
    @Input() simpleModeStyle = false;

    // 处于侧滑窗口当中
    @Input() inSlideWindow: false;
    @Input() vstoreId: string;
    @Output() operation = new EventEmitter<any>();
    @ViewChild('dt') dt: DataTableComponent;

    i18n = this.str.i18n;

    vm: FormGroup;
    errorMessage: { [key: string]: { [key: string]: string } };

    data: DnsService;

    dnsVm: FormGroup;
    dnsErrorMsg = {};
    maxDnsNum = MAXDNSNUMBER;
    SWITCH = SWITCH;
    /**
     * 标记值，作为标识当前dns域的序号
     */
    currentDnsNum = 0;
    dnsListData: DnsDomains[] = [];
    dnsTableConfig: TableConfig;
    dnsTableColumnDefs: ColumnDef[];
    hasDnsRemove = false;
    /**
     * 【DNS域名】表单项
     */
    formItems = {
        domainName: {
            valid: [
                this.str.domainNameValidator(),
            ],
            errorMsg: {
                repeat: this.i18n.domainNamevalid,
                domainName: this.i18n.domainNamevalid,
            },
        },
    };

    isSimpleMode: boolean;

    constructor(
        private service: FileDnsServiceService,
        private promptService: PromptService,
        private batchService: BatchService,
        private str: FileDnsServiceStrService,
    ) { }

    ngOnInit(): void {
        this.isSimpleMode = SWITCH.IS_SIMPLE_MODE || this.simpleModeStyle;

        this.vm = new FormGroup({
            primaryDnsIp: new FormControl('', {
                validators: [DMValidators.ipWithMulticast],
            }),
            standbyDnsIp1: new FormControl('', {
                validators: [DMValidators.ipWithMulticast],
            }),
            standbyDnsIp2: new FormControl('', {
                validators: [DMValidators.ipWithMulticast],
            }),
        }, { validators: [DMValidators.repeat()], updateOn: 'blur' });
        this.errorMessage = {
            primaryDnsIp: {
                ip: this.i18n.ipErr,
                repeat: this.i18n.repeatErr,
            },
            standbyDnsIp1: {
                ip: this.i18n.ipErr,
                repeat: this.i18n.repeatErr,
            },
            standbyDnsIp2: {
                ip: this.i18n.ipErr,
                repeat: this.i18n.repeatErr,
            },
        };
        this.initForm();
        this.initDnsTable();
    }

    /**
     * 初始化DNS配置表单
     */
    initForm(): void {
        this.dnsVm = new FormGroup({}, { validators: [DMValidators.repeat(null, null, null, null, false)], updateOn: 'blur' });
        this.initDns();
    }

    // 初始化DNS配置
    initDns(): void {
        this.service.getDnsServer(this.vstoreId || '0', { mask: true })
            .subscribe(({ data }: { data: DnsService }) => {
                const ips = data.ADDRESS ? JSON.parse(data.ADDRESS) : [];
                this.data = data;
                if (data.domains) {
                    this.initFormValue(data.domains.split(','));
                }
                this.vm.patchValue({
                    primaryDnsIp: ips[0] || '',
                    standbyDnsIp1: ips[1] || '',
                    standbyDnsIp2: ips[2] || '',
                });
                this.dnsVm.restore();
                this.vm.restore();
            });
    }

    /**
     * 初始化DNS表单的值
     */
    initFormValue(arr: string[]): void {
        arr.forEach((itemValue: string, index: number) => {
            Object.keys(this.formItems).forEach((item: string) => {
                const valid = this.formItems[item].valid;
                this.dnsVm.addControl(item + index, new FormControl(itemValue, { validators: valid, updateOn: 'change' }));
                this.dnsErrorMsg[item + index] = this.formItems[item].errorMsg;
            });
            this.dnsListData.push({
                domainName: arr[index],
                number: index,
            });
            this.currentDnsNum = index + 1;
        });

        this.dt.updateData(this.dnsListData, false);
    }

    /**
     * 新增DNS域名行
     */
    addDnsListData(): void {
        if (this.dnsListData.length >= this.maxDnsNum) {
            return;
        }
        const itemnums = this.currentDnsNum;
        Object.keys(this.formItems).forEach((item: string) => {
            const valid = this.formItems[item].valid;
            this.dnsVm.addControl(item + itemnums, new FormControl('', valid));
            this.dnsErrorMsg[item + itemnums] = this.formItems[item].errorMsg;
        });

        this.dnsListData.push({
            domainName: '',
            number: this.currentDnsNum,
        });
        this.currentDnsNum++;
        this.dt.updateData(this.dnsListData, false);
    }

    /**
     * 初始化DNS表格
     */
    initDnsTable(): void {
        this.dnsTableColumnDefs = [
            {
                field: 'domainName',
                header: this.i18n.domainName,
                hideTextTips: true,
            },
            {
                field: 'operation',
                header: this.i18n.operation,
                width: 160,
            },
        ];
        this.dnsTableConfig = {
            rows: null,
            motion: false,
        };
    }

    canLeave(): boolean {
        return !this.vm.rowValueChanged && !this.dnsVm.rowValueChanged;
    }

    // 测试IP
    test(controlName): void {
        (this.vm.get(controlName) as any).validate((valid) => {
            if (!valid) {
                return;
            }

            const params: DnsService = {
                ADDRESS: JSON.stringify([this.vm.value[controlName]]),
            };

            if (this.vstoreId) {
                params.vstoreId = this.vstoreId;
            }

            this.service.testDnsServer(params,
                {
                    name: this.str.testName(this.vm.get(controlName).value),
                    mask: true,
                    doSuccess: true,
                },
            ).subscribe();
        });
    }

    /**
     * 上移DNS行
     */
    moveUpDns(index: number, rowData: DnsDomains): void {
        if (index === 0) {
            return;
        }

        this.moveDnsDate(index, rowData, index - 1);
    }
    /**
     * 下移DNS行
     */
    moveDownDns(index: number, rowData: DnsDomains): void {
        if (index === this.maxDnsNum) {
            return;
        }

        this.moveDnsDate(index, rowData, index + 1);
    }


    moveDnsDate(index: number, rowData: DnsDomains, nextIndex: number): void {
        // 获取当前control，暂存
        const currentControl: AbstractControl = this.dnsVm.get(controlKeyPrefix + this.dnsListData[index][indexKey]);
        const nextControl: AbstractControl = this.dnsVm.get(controlKeyPrefix + this.dnsListData[nextIndex][indexKey]);
        // dnsVm中删除当前control
        this.dnsVm.removeControl(controlKeyPrefix + this.dnsListData[index][indexKey]);
        this.dnsVm.removeControl(controlKeyPrefix + this.dnsListData[nextIndex][indexKey]);
        // 错误描述列表中中删除当前项
        delete this.dnsErrorMsg[controlKeyPrefix + this.dnsListData[index][indexKey]];
        delete this.dnsErrorMsg[controlKeyPrefix + this.dnsListData[nextIndex][indexKey]];
        // 更新移动后的DNSListData数据
        this.dnsListData = this.dnsListData.map((dnsDomainItem: DnsDomains, arrIndex) => {
            let item = dnsDomainItem;
            if (arrIndex === nextIndex) {
                item = rowData;
                item.number = arrIndex;
            }
            if (arrIndex === index) {
                item = this.dnsListData[nextIndex];
                item.number = arrIndex;
            }
            return item;
        });
        // 根据移动后的dnsListData中number对应的值，获得新的key；把新的key与对应暂存的control加回dnsVm
        this.dnsVm.addControl(controlKeyPrefix + this.dnsListData[index][indexKey], nextControl);
        this.dnsVm.addControl(controlKeyPrefix + this.dnsListData[nextIndex][indexKey], currentControl);
        // 根据移动后的dnsListData中number对应的值，获得新的key；增加对应key与错误描述添加到错误描述列表中
        this.dnsErrorMsg[controlKeyPrefix + this.dnsListData[index][indexKey]] = this.formItems.domainName.errorMsg;
        this.dnsErrorMsg[controlKeyPrefix + this.dnsListData[nextIndex][indexKey]] = this.formItems.domainName.errorMsg;
        this.initDnsTable();
    }

    /**
     * 移除DNS行
     */
    deleteDns(index: number): void {
        Object.keys(this.formItems).forEach((value: string) => {
            this.dnsVm.removeControl(value + index);
            delete this.dnsErrorMsg[value + index];
        });
        this.dnsListData.splice(index, 1);
        this.dt.updateData(this.dnsListData, false);
        this.hasDnsRemove = true;
        this.reformateTables();
        this.initDnsTable();
    }

    /**
     * 移除DNS行后对数据进行清洗，避免移除后上下移动引发number重复问题
     */
    reformateTables(): void {
        // 对dnsListData数据清洗
        this.dnsListData.forEach((item, index) => {
            item.number = index;
        });
        // 对dnsVm和dnsErrorMsg进行数据清洗
        const controlValues: ControlValer = this.dnsVm.value;
        const controlKeys = Object.keys(controlValues).sort();
        controlKeys.forEach((controlKey, index) => {
            const controlSnap: AbstractControl = this.dnsVm.get(controlKey);
            this.dnsVm.removeControl(controlKey);
            this.dnsVm.addControl(controlKeyPrefix + index, controlSnap);

            delete this.dnsErrorMsg[controlKey];
            this.dnsErrorMsg[controlKeyPrefix + index] = this.formItems.domainName.errorMsg;
        });
    }

    // 测试所有
    testAll(): void {
        this.vm.validate((valid) => {
            if (!valid) {
                return;
            }

            const cmds: Command[] = [];

            if (this.vm.value.primaryDnsIp) {
                const params: DnsService = {
                    ADDRESS: JSON.stringify([this.vm.value.primaryDnsIp]),
                };

                if (this.vstoreId) {
                    params.vstoreId = this.vstoreId;
                }
                cmds.push({
                    name: this.i18n.testRest,
                    nameParams: [this.vm.value.primaryDnsIp],
                    action: this.service.testDnsServer(params, { doException: false }),
                });
            }
            if (this.vm.value.standbyDnsIp1) {
                const params: DnsService = {
                    ADDRESS: JSON.stringify([this.vm.value.standbyDnsIp1]),
                };

                if (this.vstoreId) {
                    params.vstoreId = this.vstoreId;
                }
                cmds.push({
                    name: this.i18n.testRest,
                    nameParams: [this.vm.value.standbyDnsIp1],
                    action: this.service.testDnsServer(params, { doException: false }),
                });
            }
            if (this.vm.value.standbyDnsIp2) {
                const params: DnsService = {
                    ADDRESS: JSON.stringify([this.vm.value.standbyDnsIp2]),
                };

                if (this.vstoreId) {
                    params.vstoreId = this.vstoreId;
                }
                cmds.push({
                    name: this.i18n.testRest,
                    nameParams: [this.vm.value.standbyDnsIp2],
                    action: this.service.testDnsServer(params, { doException: false }),
                });
            }

            this.batchService.exePrompt(cmds);
        });
    }

    // 保存
    onSave(): void {
        if (!this.vm.rowValueChanged && !this.dnsVm.rowValueChanged && !this.hasDnsRemove && this.dnsErrorMsg) {
            return;
        }

        this.dnsVm.validateOb().subscribe();
        this.vm.validate((valid) => {
            if (!valid || !this.dnsVm.valid) {
                return;
            }

            const ips = [];

            if (this.vm.value.primaryDnsIp) {
                ips.push(this.vm.value.primaryDnsIp);
            }
            if (this.vm.value.standbyDnsIp1) {
                ips.push(this.vm.value.standbyDnsIp1);
            }
            if (this.vm.value.standbyDnsIp2) {
                ips.push(this.vm.value.standbyDnsIp2);
            }

            let domainNameArr = [];
            this.dnsListData.forEach((item) => {
                domainNameArr = [...domainNameArr, ...[item.domainName]];
            });

            const params: DnsService = {
                ADDRESS: JSON.stringify(ips),
                domains: domainNameArr.join(','),
            };
            if (this.vstoreId) {
                params.vstoreId = this.vstoreId;
            }
            this.service.setDnsServer(params, { doSuccess: true, mask: true })
                .subscribe(() => {
                    this.initDns();
                    if (this.inSlideWindow) {
                        this.operation.emit();
                    }
                });
        });
    }

    // 取消
    onCancel(): void {
        if (this.inSlideWindow) {
            this.operation.emit();
            return;
        }

        if (!this.vm.rowValueChanged && !this.dnsVm.rowValueChanged) {
            return;
        }

        this.promptService.promptConfirm({
            title: this.i18n.cancelBtn,
            content: this.i18n.cancelMsg,
            onOk: () => {
                this.vm.resetToLast();
                this.vm.clearValidate();
                this.promptService.close();
            },
        });
    }
}
